/**
 * Un identificador único, en cualquier navegador y por cualquier conexión.
 *
 * ── Por qué no basta con `crypto.randomUUID()` ──────────────────────────────
 *
 * Esa función existe, pero se cae en dos sitios donde esta app sí se usa, y
 * cuando se cae LANZA — no devuelve algo raro. El resultado es que crear un
 * estimado, una tarea, un material o una factura no hace nada, sin mensaje.
 *
 *   1. Safari entre la 14 y la 15.3. En la práctica: iPhone 6s, 7 y SE de
 *      primera generación sin actualizar. Los más viejos ni cargan la app.
 *
 *   2. Y este importa más de lo que parece: `crypto.subtle` y `randomUUID`
 *      solo existen en un CONTEXTO SEGURO. `localhost` cuenta como seguro,
 *      pero `http://192.168.1.x:3008` NO. Y `vite.config.ts` abre el servidor
 *      a la red (`host: '0.0.0.0'`) justamente para poder probar desde el
 *      teléfono, que es cuando más falta hace. Probar en el móvil por IP era
 *      exactamente el escenario en que todo se rompía.
 *
 * El repo ya conocía este problema por otro lado: `timeclock-utils.ts` protege
 * la geolocalización contra el mismo caso. Aquella degrada limpio; esto
 * reventaba.
 *
 * ── Sobre el respaldo ───────────────────────────────────────────────────────
 *
 * Se prefiere SIEMPRE la función del navegador cuando existe. El respaldo usa
 * `crypto.getRandomValues` si lo hay —que sí está en contextos inseguros y en
 * Safari desde hace más de una década— y solo cae en `Math.random` si tampoco.
 *
 * El formato es un UUID v4 válido en los tres casos, porque estos ids viajan a
 * columnas `uuid` de Postgres y una cadena con otra forma la rechaza la base.
 */
export function nuevoId(): string {
    const c = globalThis.crypto as Crypto | undefined;

    if (typeof c?.randomUUID === 'function') {
        try {
            return c.randomUUID();
        } catch {
            /* Documentado en MDN: en un contexto inseguro puede existir y aun
               así lanzar. Se sigue hacia el respaldo en vez de romper. */
        }
    }

    // 16 bytes al azar, con los bits de versión y variante que pide un UUID v4.
    const b = new Uint8Array(16);
    if (typeof c?.getRandomValues === 'function') {
        c.getRandomValues(b);
    } else {
        for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
    }
    b[6] = (b[6] & 0x0f) | 0x40;   // versión 4
    b[8] = (b[8] & 0x3f) | 0x80;   // variante RFC 4122

    const hex: string[] = [];
    for (let i = 0; i < 256; i++) hex.push((i + 0x100).toString(16).slice(1));
    return (
        hex[b[0]] + hex[b[1]] + hex[b[2]] + hex[b[3]] + '-' +
        hex[b[4]] + hex[b[5]] + '-' +
        hex[b[6]] + hex[b[7]] + '-' +
        hex[b[8]] + hex[b[9]] + '-' +
        hex[b[10]] + hex[b[11]] + hex[b[12]] + hex[b[13]] + hex[b[14]] + hex[b[15]]
    );
}
